import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { searchCompanies, formatCurrency, DEFAULT_YEAR, checkPodRiskByCompanyBatch } from "../utils/cmsApi";
import { useSubscription } from "../context/SubscriptionContext";
import { useUser } from "@clerk/react";
import { useMyProviders } from "../context/MyProvidersContext";
import { watchCompany, unwatchCompany, getWatchedCompanies, queueConfirmation } from "../utils/alertsApi";
import CompanyCompareButton from "../components/CompanyCompareButton";

export default function CompanyResultsPage({ year = DEFAULT_YEAR }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isPro, getResultLimit, setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const { user, isSignedIn } = useUser();
  const { filterActive, selectedListIdsString, selectedLists, totalSelectedProviders } = useMyProviders();
  const query = searchParams.get("q") || "";

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [watchedCompanyNames, setWatchedCompanyNames] = useState(new Map());
  const [companyWatchLoading, setCompanyWatchLoading] = useState(new Set());

  const [myProviderCounts, setMyProviderCounts] = useState(new Map());
  const [podCompanyIds, setPodCompanyIds] = useState(new Map());

  useEffect(() => {
    if (!query) return;
    setLoading(true);
    setError(null);
    setMyProviderCounts(new Map());

    searchCompanies(query, year)
      .then((data) => {
        setResults(data);
        // Batch-check POD risk for all returned company IDs
        const companyIds = data.map((c) => c.companyId).filter(Boolean);
        if (companyIds.length > 0) {
          checkPodRiskByCompanyBatch(companyIds)
            .then((items) => setPodCompanyIds(new Map(items.map((i) => [i.companyId, i.podTier]))))
            .catch(() => {});
        }
        // If filter is active, fetch my-provider counts for each company
        if (filterActive && selectedListIdsString && user?.id && data.length > 0) {
          fetch(`/api/bigquery?action=getMyProviderCountsByCompany&userId=${encodeURIComponent(user.id)}&listIds=${encodeURIComponent(selectedListIdsString)}&companyIds=${encodeURIComponent(companyIds.join(","))}&year=${year}`)
            .then((r) => r.json())
            .then((counts) => {
              // counts = [{ companyId, myProviderCount, myTotalPaid, myPaymentCount }]
              const map = new Map((counts || []).map((c) => [c.companyId, c]));
              setMyProviderCounts(map);
            })
            .catch(() => {});
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [query, year, filterActive, selectedListIdsString, user?.id]);

  useEffect(() => {
    if (!isSignedIn || !user) return;
    getWatchedCompanies(user.id)
      .then((data) => {
        setWatchedCompanyNames(new Map(data.map((c) => [c.company_name, true])));
      })
      .catch(() => {});
  }, [isSignedIn, user]);

  async function handleCompanyWatchToggle(e, companyName, companyId) {
    e.preventDefault();
    e.stopPropagation();
    if (!isSignedIn) {
      setUpgradeMessage("Sign in to watch companies and receive email alerts.");
      setShowUpgradeModal(true);
      return;
    }
    if (!isPro) {
      setUpgradeMessage("Watching companies and receiving email alerts is a Pro feature. Upgrade to get notified when new payment data is published.");
      setShowUpgradeModal(true);
      return;
    }
    setCompanyWatchLoading((prev) => new Set(prev).add(companyName));
    try {
      if (watchedCompanyNames.get(companyName)) {
        await unwatchCompany(user.id, companyName);
        setWatchedCompanyNames((prev) => new Map(prev).set(companyName, false));
      } else {
        await watchCompany(user.id, companyName, companyId);
        await queueConfirmation(user.id, "company", companyName, companyName, { alert_payments: true });
        setWatchedCompanyNames((prev) => new Map(prev).set(companyName, true));
      }
    } catch (err) {
      console.error("Company watch toggle error:", err);
    } finally {
      setCompanyWatchLoading((prev) => { const s = new Set(prev); s.delete(companyName); return s; });
    }
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-10">
      <div className="mb-6">
        <Link to="/" className="text-white/40 hover:text-white/70 text-sm transition-colors">
          ← Home
        </Link>
        <h1 className="font-display text-3xl text-white mt-3">
          Companies matching{" "}
          <span className="text-gold-400">"{query}"</span>
        </h1>
        {!loading && !error && (
          <p className="text-white/40 text-sm mt-1">
            {Math.min(results.length, getResultLimit())} of {results.length} {results.length === 1 ? "company" : "companies"} shown · {year} data
            {!isPro && results.length > getResultLimit() && (
              <span className="text-gold-400 text-xs ml-2">
                · Upgrade to see all results
              </span>
            )}
          </p>
        )}
      </div>

      {/* My Providers filter banner */}
      {filterActive && (
        <div className="mb-4 flex items-center justify-between px-3 py-2 rounded-lg bg-gold-500/10 border border-gold-500/30">
          <div className="flex items-center gap-2">
            <span className="text-gold-400 text-xs">◉</span>
            <span className="text-gold-400 text-xs font-medium">
              {selectedLists.length === 1
                ? selectedLists[0].list_name
                : `${selectedLists.length} lists`}
            </span>
            <span className="text-gold-400/50 text-xs">
              · {totalSelectedProviders.toLocaleString()} providers
            </span>
          </div>
          <Link to="/my-providers" className="text-gold-400/60 hover:text-gold-400 text-xs transition-colors">
            Manage →
          </Link>
        </div>
      )}

      {loading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-4 bg-white/10 rounded w-1/2 mb-3" />
              <div className="h-3 bg-white/5 rounded w-3/4 mb-2" />
              <div className="h-3 bg-white/5 rounded w-1/4" />
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="card border-red-500/20 bg-red-500/5 text-red-300 text-sm">
          ⚠ {error}
        </div>
      )}

      {!loading && !error && results.length === 0 && (
        <div className="card text-center py-12">
          <div className="text-4xl mb-3 text-white/20">◎</div>
          <p className="text-white/50 text-sm">No companies found matching "{query}".</p>
          <p className="text-white/30 text-xs mt-1">Try a different search term.</p>
        </div>
      )}

      <div className="space-y-3">
        {results.slice(0, getResultLimit()).map((company) => (
          <div
            key={company.companyName}
            onClick={() => {
              if (!isPro) {
                setUpgradeMessage("Company profiles — including payment trends, ownership data, research payments, and full provider lists — are a Pro feature. Upgrade to access the full company profile.");
                setShowUpgradeModal(true);
                return;
              }
              navigate(`/company/${encodeURIComponent(company.companyId)}`);
            }}
            className="card block hover:border-gold-500/30 hover:bg-navy-700/50 transition-all group cursor-pointer"
          >
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-white font-medium group-hover:text-gold-300 transition-colors">
                  {company.companyName}
                </h2>
                <p className="text-white/40 text-sm mt-0.5">
                  {(() => {
                    const myData = filterActive ? myProviderCounts.get(company.companyId) : null;
                    if (filterActive && myData) {
                      return `${myData.myProviderCount.toLocaleString()} of your ${myData.myProviderCount === 1 ? "provider" : "providers"} paid · ${company.providerCount.toLocaleString()} total`;
                    }
                    if (company.providerCount > 0) {
                      return `${company.providerCount.toLocaleString()} ${company.providerCount === 1 ? "provider" : "providers"} paid`;
                    }
                    if (company.hasOwnership || company.hasResearch) {
                      return "No general payments · see ownership or research data";
                    }
                    return "No payment data for this year";
                  })()}
                </p>
              </div>
              <span className="text-white/30 group-hover:text-white/60 ml-4 mt-1">›</span>
            </div>
            <div className="flex gap-3 mt-3 flex-wrap">
              {(() => {
                const myData = filterActive ? myProviderCounts.get(company.companyId) : null;
                const displayTotal = myData ? myData.myTotalPaid : company.totalPayments;
                const displayCount = myData ? myData.myPaymentCount : company.paymentCount;
                return (
                  <>
                    {displayTotal > 0 && (
                      <span className="badge bg-gold-500/10 border border-gold-500/20 text-gold-300">
                        {formatCurrency(displayTotal)}
                        {myData && <span className="text-gold-400/50 ml-1 text-xs">your providers</span>}
                      </span>
                    )}
                    {displayCount > 0 && (
                      <span className="badge bg-white/5 border border-white/10 text-white/50">
                        {displayCount.toLocaleString()} payments
                        {myData && <span className="text-white/30 ml-1 text-xs">your providers</span>}
                      </span>
                    )}
                    {filterActive && (
                      myProviderCounts.size === 0
                        ? <span className="badge bg-white/5 border border-white/10 text-white/20 text-xs animate-pulse">◉ checking...</span>
                        : <span className={`badge text-xs ${myData ? "bg-gold-500/20 border border-gold-500/40 text-gold-300" : "bg-white/5 border border-white/10 text-white/25"}`}>
                            ◉ {myData ? myData.myProviderCount.toLocaleString() : "0"} of your providers
                          </span>
                    )}
                  </>
                );
              })()}
              {company.hasGeneralPayments && (
                <span className="badge bg-gold-500/10 border border-gold-500/20 text-gold-400/80 text-xs">
                  💊 Payments
                </span>
              )}
              {company.hasOwnership && (
                <span className="badge bg-amber-500/10 border border-amber-500/20 text-amber-400/80 text-xs">
                  🏦 Ownership
                </span>
              )}
              {company.hasResearch && (
                <span className="badge bg-teal-500/10 border border-teal-500/20 text-teal-400/80 text-xs">
                  🔬 Research
                </span>
              )}
              {podCompanyIds.has(company.companyId) && (
                <span className="badge bg-blue-500/10 border border-blue-500/30 text-blue-400/80 text-xs">
                  ⚠ POD Risk Tier {podCompanyIds.get(company.companyId)}
                </span>
              )}
            </div>
            <div className="mt-3 flex justify-end items-center gap-3">
              {!isPro && (
                <span className="text-white/20 text-xs">🔒 Pro — view full profile</span>
              )}
              <CompanyCompareButton companyId={company.companyId} companyName={company.companyName} />
              <button
                onClick={(e) => handleCompanyWatchToggle(e, company.companyName, company.companyId)}
                disabled={companyWatchLoading.has(company.companyName)}
                className={`text-xs px-3 py-1 rounded-lg border transition-all ${
                  watchedCompanyNames.get(company.companyName)
                    ? "bg-gold-500/20 border-gold-500/40 text-gold-300"
                    : "border-white/20 text-white/40 hover:border-gold-500/40 hover:text-gold-300"
                }`}
              >
                {companyWatchLoading.has(company.companyName)
                  ? "..."
                  : watchedCompanyNames.get(company.companyName)
                  ? "🔔 Watching"
                  : "🔕 Watch"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}